import {
  OreCheckbox,
  OreIconButton,
  OreStack,
  OreTableCell,
  OreTableRow,
  OreText,
  OreTooltip,
  SvgExcel,
  SvgPdf,
} from '@edenredespana/oreneta'
import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceModel } from '@/Invoices/domain'
import { PillState } from '@/Invoices/ui/atoms'
import { useInvoice } from '@/Invoices/ui/context'
import { invoicesTranslation } from '@/Invoices/ui/invoicesTranslation'
import { GetDateFormatted, currency } from '@/Shared/domain'
import { useUserData } from '@/User/ui/context'

interface IProps {
  invoice: InvoiceModel
  selected: boolean
  isHome: boolean | undefined
  handleSelect: (id: number) => void
}

export const InvoiceRow = ({
  invoice,
  selected,
  isHome,
  handleSelect,
}: IProps): JSX.Element => {
  const { t } = useTranslation()
  const { merchant } = useUserData()
  const {
    merchantName,
    issueDate,
    invoiceNumber,
    state,
    firstTransactionDate,
    lastTransactionDate,
    numberOfTransactions,
    generatedAmount,
    totalAmount,
    invoiceId,
    isInvoiceReadyToDownload,
    isTransactionReadyToDownload,
    isInvoiceDownloadable,
    productCode,
  } = invoice

  const isNursery = merchant.isNursery
  const isChildCommercial = merchant.isChildCommercial
  const isParentCommercial = merchant.isParentCommercial
  const isParentWithHardDependency = merchant.isParentWithHardDependency
  const isIndependent = merchant.isIndependent
  const isChild = merchant.isChild

  const firstUseProfile =
    isChildCommercial || isParentCommercial || isParentWithHardDependency

  const { downloadPdf, downloadTransactions } = useInvoice()

  // Componente que muestra texto truncado a 2 líneas (ellipsis) y,
  // sólo si realmente se está truncando, envuelve el contenido en un tooltip
  // con el texto completo.
  const TwoLineEllipsisText = ({ text }: { text?: string }) => {
    // Referencia al <span> que aplica el line-clamp de 2 líneas.
    const spanRef = useRef<HTMLSpanElement | null>(null)
    // Estado que indica si el contenido excede 2 líneas (si hay truncado).
    const [isOverflow, setIsOverflow] = useState(false)
    // Contenedor oculto reutilizable para medir alturas sin clamp.
    const measureContainerRef = useRef<HTMLDivElement | null>(null)
    // RAF para agrupar re-medidas y evitar medir demasiadas veces.
    const rafIdRef = useRef<number | null>(null)
    // Observer para limpiar correctamente.
    const roRef = useRef<ResizeObserver | null>(null)

    useLayoutEffect(() => {
      const el = spanRef.current
      if (!el || typeof window === 'undefined') return

      // Crear contenedor oculto una sola vez y reutilizarlo en cada medición.
      const container = document.createElement('div')
      measureContainerRef.current = container
      container.style.position = 'absolute'
      container.style.visibility = 'hidden'
      container.style.pointerEvents = 'none'
      container.style.whiteSpace = 'normal'
      container.style.wordBreak = 'break-word'
      container.style.boxSizing = 'border-box'
      document.body.appendChild(container)

      // Función de medición: calcula si el texto ocupa más de dos líneas
      // sin aplicar clamp, comparando altura natural vs. altura de dos líneas.
      const measure = () => {
        const element = spanRef.current
        const c = measureContainerRef.current
        if (!element || !c) return

        const style = window.getComputedStyle(element)
        const width =
          element.getBoundingClientRect().width ||
          element.clientWidth ||
          element.offsetWidth
        c.style.width = `${Math.ceil(width)}px`
        c.style.fontFamily = style.fontFamily
        c.style.fontSize = style.fontSize
        c.style.fontWeight = style.fontWeight as string
        c.style.lineHeight = style.lineHeight
        c.style.letterSpacing = style.letterSpacing

        // Altura natural del texto (sin clamp)
        const trimmed = (text || '').trim()
        c.textContent = trimmed
        const naturalHeight = c.scrollHeight

        // Altura de dos líneas: si la line-height está en px, calculamos rápido.
        // Si no, medimos con dos líneas de texto.
        let twoLineHeight: number
        const lh = style.lineHeight
        const isPx = lh.endsWith('px')
        const pxValue = isPx ? Number.parseFloat(lh) : NaN
        if (Number.isFinite(pxValue)) {
          twoLineHeight = Math.round(pxValue * 2)
        } else {
          c.innerHTML = 'W<br/>W'
          twoLineHeight = c.scrollHeight
          c.textContent = trimmed
        }

        const overflowClamped = naturalHeight - twoLineHeight > 0.5
        setIsOverflow(overflowClamped)
      }

      // Agrupar mediciones en el próximo frame para evitar repeticiones.
      const scheduleMeasure = () => {
        if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = requestAnimationFrame(measure)
      }

      // Medición inicial tras el layout.
      scheduleMeasure()
      // Re-medición cuando las fuentes están listas (si el navegador lo soporta).
      if ((document as any).fonts?.ready) {
        ;(document as any).fonts.ready.then(scheduleMeasure).catch(() => {})
      }

      // Observadores de cambio de tamaño: elemento, su padre y la celda de tabla.
      // Así reaccionamos a cambios de ancho del contenedor.
      const ro = new ResizeObserver(() => scheduleMeasure())
      ro.observe(el)
      if (el.parentElement) ro.observe(el.parentElement)
      const nearestCell = el.closest(
        'td, [role="cell"],[data-ore-table-cell]',
      ) as Element | null
      if (nearestCell) ro.observe(nearestCell)
      roRef.current = ro

      // También escuchar cambios de tamaño de la ventana.
      const onWindowResize = () => scheduleMeasure()
      window.addEventListener('resize', onWindowResize)

      return () => {
        if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current)
        if (roRef.current) roRef.current.disconnect()
        if (measureContainerRef.current) {
          try {
            document.body.removeChild(measureContainerRef.current)
          } catch {}
          measureContainerRef.current = null
        }
        window.removeEventListener('resize', onWindowResize)
      }
    }, [text])

    // Contenido visual: texto tipográfico y <span> con CSS para clamping a 2 líneas.
    const content = (
      <OreText size="text-sm">
        <span
          ref={spanRef}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {(text || '').trim()}
        </span>
      </OreText>
    )
    // Si hay truncado real (overflow), mostrar tooltip con el texto completo;
    // si no, renderizar el contenido tal cual sin tooltip.
    return isOverflow ? (
      <OreTooltip text={text || ''} placement="top" separation="small">
        {content}
      </OreTooltip>
    ) : (
      content
    )
  }

  return (
    <OreTableRow valign="middle">
      {!isHome ? (
        <OreTableCell>
          <OreCheckbox
            id={invoiceNumber}
            checked={selected}
            onChange={() => handleSelect(invoiceId)}
          />
        </OreTableCell>
      ) : null}
      {!isNursery && !isIndependent && !isHome ? (
        <OreTableCell>
          <OreStack space="small">
            <TwoLineEllipsisText text={merchantName} />
          </OreStack>
        </OreTableCell>
      ) : null}
      {!isNursery && firstUseProfile && !isHome ? null : (
        <OreTableCell>
          <OreStack space="small">
            <OreText size="text-sm">{GetDateFormatted(issueDate)}</OreText>
          </OreStack>
        </OreTableCell>
      )}
      {isNursery &&
      !isIndependent &&
      !isHome &&
      !isParentWithHardDependency &&
      !isChild ? (
        <OreTableCell>
          <OreStack space="small">
            <TwoLineEllipsisText text={merchantName} />
          </OreStack>
        </OreTableCell>
      ) : null}
      <OreTableCell>
        <OreStack space="small">
          <OreText align="right" size="text-sm">
            {invoiceNumber}
          </OreText>
        </OreStack>
      </OreTableCell>
      <OreTableCell>
        <OreStack space="small">
          <PillState state={state} />
        </OreStack>
      </OreTableCell>
      {isNursery ? null : (
        <OreTableCell>
          <OreStack space="small">
            <OreText size="text-sm">
              {GetDateFormatted(firstTransactionDate)} -{' '}
              {GetDateFormatted(lastTransactionDate)}
            </OreText>
          </OreStack>
        </OreTableCell>
      )}

      {!merchant.hasFlatRateCommission && (
        <>
          {/* Todas tienen que estar alineadas a la izquierda 
        Excepto las que tienen cantidades de dinero, esas a la derecha*/}
          <OreTableCell>
            <OreStack placeContent="start" space="small">
              <OreText align="right" size="text-sm">
                {numberOfTransactions}
              </OreText>
            </OreStack>
          </OreTableCell>
          {isNursery ? null : (
            <OreTableCell>
              <OreStack placeContent="end" space="small">
                <OreText align="right" size="text-sm">
                  {currency(generatedAmount)}
                </OreText>
              </OreStack>
            </OreTableCell>
          )}
        </>
      )}

      <OreTableCell>
        <OreStack placeContent="end" space="small">
          <OreText align="right" size="text-sm">
            {currency(totalAmount)}
          </OreText>
        </OreStack>
      </OreTableCell>
      <OreTableCell>
        <OreStack space="small" direction="row">
          {isInvoiceDownloadable && isInvoiceReadyToDownload ? (
            <OreTooltip
              text={t(invoicesTranslation.table.body.actions.downloadInvoice)}
              placement="top"
              separation="small"
            >
              <OreIconButton
                icon={<SvgPdf />}
                onClick={() => downloadPdf(invoiceId, invoiceNumber)}
                aria-label={t(invoicesTranslation.table.body.actions.downloadInvoice)}
              />
            </OreTooltip>
          ) : (
            <OreTooltip
              text={
                !isInvoiceDownloadable
                  ? t(invoicesTranslation.table.body.tooltips.notDownloadable)
                  : !isInvoiceReadyToDownload
                    ? t(invoicesTranslation.table.body.tooltips.notReadyDownload)
                    : ''
              }
              placement="top"
              separation="small"
            >
              <OreIconButton icon={<SvgPdf />} disabled={true} />
            </OreTooltip>
          )}
          {isTransactionReadyToDownload ? (
            <OreTooltip
              text={t(invoicesTranslation.table.body.actions.downloadTransactions)}
              placement="top"
              separation="small"
            >
              <OreIconButton
                icon={<SvgExcel />}
                onClick={() =>
                  downloadTransactions(invoiceId, invoiceNumber, productCode)
                }
                aria-label={t(
                  invoicesTranslation.table.body.actions.downloadTransactions,
                )}
              />
            </OreTooltip>
          ) : (
            <OreTooltip
              text={t(invoicesTranslation.table.body.tooltips.notReadyTransactions)}
              placement="top"
              separation="small"
            >
              <OreIconButton
                icon={<SvgExcel />}
                disabled={!isTransactionReadyToDownload}
                onClick={() =>
                  downloadTransactions(invoiceId, invoiceNumber, productCode)
                }
              />
            </OreTooltip>
          )}
        </OreStack>
      </OreTableCell>
    </OreTableRow>
  )
}
